import React, { useEffect, forwardRef } from 'react';
import styled from 'styled-components';
import { FaTimes, FaCheckCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';

const alertTypes = {
  success: {
    icon: FaCheckCircle,
    bg: '#E8F5E9',
    border: '#4CAF50',
    color: '#1B5E20'
  },
  error: {
    icon: FaExclamationTriangle,
    bg: '#FFEBEE',
    border: '#F44336',
    color: '#C62828'
  },
  warning: {
    icon: FaExclamationTriangle,
    bg: '#FFF3E0',
    border: '#FF9800',
    color: '#E65100'
  },
  info: {
    icon: FaInfoCircle,
    bg: '#E3F2FD',
    border: '#2196F3',
    color: '#0D47A1'
  }
};

const AlertContainer = styled.div`
  padding: 1rem 1.2rem;
  margin: 1rem 0;
  border-radius: 12px;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  animation: slideIn 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  background: ${props => alertTypes[props.$type].bg};
  border-left: 4px solid ${props => alertTypes[props.$type].border};
  color: ${props => alertTypes[props.$type].color};

  @media (max-width: 480px) {
    padding: 0.85rem;
    gap: 0.6rem;
  }
`;

const IconWrapper = styled.div`
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

// min-width: 0 deixa o texto quebrar em vez de alargar a caixa (URLs longas etc.).
const Content = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Title = styled.h4`
  margin: 0 0 0.3rem 0;
  font-size: 1rem;
  font-weight: 600;
`;

const Message = styled.p`
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  flex-shrink: 0;
  border: none;
  color: currentColor;
  opacity: 0.6;
  cursor: pointer;
  padding: 0.2rem;
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.05);
  }
`;

/**
 * @param {number} [props.autoCloseTime=5000] - Tempo em ms para fechamento automático (usado com autoClose)
 */
export const Alert = forwardRef(({
  type = 'info',
  title,
  message,
  onClose,
  autoClose,
  autoCloseTime = 5000,
  ...props
}, ref) => {
  useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(onClose, autoCloseTime);
      return () => clearTimeout(timer);
    }
  }, [autoClose, onClose, autoCloseTime]);

  // Garante que tipos inválidos nunca quebrem o componente, caindo para 'info'
  const safeType = alertTypes[type] ? type : 'info';
  const Icon = alertTypes[safeType].icon;

  return (
    <AlertContainer ref={ref} $type={safeType} {...props}>
      <IconWrapper>
        <Icon />
      </IconWrapper>

      <Content>
        {title && <Title>{title}</Title>}
        <Message>{message}</Message>
      </Content>

      {onClose && (
        <CloseButton onClick={onClose} aria-label="Fechar">
          <FaTimes size={16} />
        </CloseButton>
      )}
    </AlertContainer>
  );
});
